import React, { useState } from 'react';
import Table, { CellProps, SortType } from 'rsuite/Table';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import Whisper from 'rsuite/Whisper';
import IconButton from 'rsuite/IconButton';
import { useNavigate } from 'react-router-dom';

import classNames from 'classnames';
import { useCustomsDeliveryMethods } from '../../../../consts/db-values';
import {
  CleanCustomsDeliveryMethod,
  CleanDeliveryMethod,
  RbacObject,
} from '../../../../export-types/cleaned-types';
import { WhisperSpeaker } from '../../../../utils/whisper-speaker';
import { Rbac } from '../../../common/Rbac';
import {
  SupplierSelect,
  useSelectSupplierQs,
} from '../../purchase-order/supplier-select';
import { SupplierCell } from '../../../common/table/supplier-cell';
import { ToggleCell } from '../../../common/table/toggle-cell';
import { trpcSwr } from '../../../../api/trpc';
import { AlertService } from '../../../../services/alert.service';

const compareNumberValues = (
  a: CleanCustomsDeliveryMethod,
  b: CleanCustomsDeliveryMethod,
  field: keyof CleanCustomsDeliveryMethod,
  sortType: SortType,
): number => {
  const aValueRaw = a?.[field];
  const bValueRaw = b?.[field];

  if (aValueRaw == null && bValueRaw == null) return 0;
  if (aValueRaw == null) return 1;
  if (bValueRaw == null) return -1;

  const aValue =
    typeof aValueRaw === 'string' ? parseFloat(aValueRaw) : aValueRaw;
  const bValue =
    typeof bValueRaw === 'string' ? parseFloat(bValueRaw) : bValueRaw;

  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return sortType === 'asc' ? aValue - bValue : bValue - aValue;
  }

  return 0;
};

export const CustomsDeliveryMethodsTab = () => {
  const DELIVERY_METHODS = useCustomsDeliveryMethods();

  const navigate = useNavigate();

  const [selectSupplier] = useSelectSupplierQs();

  const { data, mutate, isValidating } =
    trpcSwr.customsDeliveryMethod.getList.useSWR({});
  const { trigger: updateMethod } =
    trpcSwr.customsDeliveryMethod.update.useSWRMutation();
  const [sortColumn, setSortColumn] = React.useState<string | undefined>();
  const [sortType, setSortType] = React.useState<SortType | undefined>();
  const [loading, setLoading] = useState<boolean>();

  const sortedAndFilteredData = React.useMemo(() => {
    let updateData = data || [];

    if (selectSupplier) {
      updateData = updateData.filter(
        (item) => item.supplier && item.supplier.id === selectSupplier,
      );
    }

    if (sortColumn && sortType && updateData.length > 0) {
      return [...updateData].sort((a, b) => {
        switch (sortColumn) {
          case 'method':
          case 'name':
          case 'disabled':
          case 'deliveryPeriodFrom':
          case 'minWeight':
          case 'maxWeight':
            return compareNumberValues(
              a,
              b,
              sortColumn as keyof CleanCustomsDeliveryMethod,
              sortType,
            );
          default:
            return 0;
        }
      });
    }

    updateData.sort((a, b) => {
      return a.deliveryPeriodFrom - b.deliveryPeriodFrom;
    });

    return updateData;
  }, [data, selectSupplier, sortColumn, sortType]);

  const handleSortColumn = (
    dataKey: string,
    sortType?: SortType | undefined,
  ) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSortColumn(dataKey);
      setSortType(sortType);
    }, 500);
  };

  async function checkDeliveryMethod(
    rowData: CleanDeliveryMethod,
    value: boolean,
  ) {
    await updateMethod({
      id: rowData.id,
      data: { disabled: value },
    });
    await mutate();

    AlertService.success();
  }

  const DeliveryMethodCell = ({
    rowData,
    ...props
  }: CellProps<CleanCustomsDeliveryMethod>) =>
    rowData && (
      <Table.Cell {...props}>
        {DELIVERY_METHODS[rowData.method as keyof typeof DELIVERY_METHODS]}
      </Table.Cell>
    );

  const PeriodCell = ({
    rowData,
    ...props
  }: CellProps<CleanCustomsDeliveryMethod>) =>
    rowData && (
      <Table.Cell {...props}>
        {rowData.deliveryPeriodFrom}
        {rowData.deliveryPeriodTo ? ` — ${rowData.deliveryPeriodTo}` : ''} дней
      </Table.Cell>
    );

  const renderMenu =
    (deliveryMethod: CleanCustomsDeliveryMethod): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = (eventKey: number | string | undefined) => {
        onClose();
        if (eventKey === 1) {
          navigate(`/customs-deliveries/delivery-methods/create`, {
            state: deliveryMethod,
          });
        }
      };
      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey={1}>
              <i className="bi bi-pencil" />
              <span className="ps-2">Изменить</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={2}>
              <i className="bi bi-trash" />
              <span className="ps-2">Удалить</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  const ActionCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<CleanCustomsDeliveryMethod>) => {
    return (
      rowData && (
        <Table.Cell
          {...props}
          className="link-group no-padding-cell pt-2"
          align="right"
        >
          <Whisper
            placement="leftStart"
            trigger="click"
            speaker={renderMenu(rowData)}
          >
            <IconButton
              appearance="subtle"
              icon={<i className="bi bi-three-dots" />}
            />
          </Whisper>
        </Table.Cell>
      )
    );
  };

  const DeliveryMethodNameCell = ({
    rowData,
    ...props
  }: CellProps<CleanCustomsDeliveryMethod>) =>
    rowData && <Table.Cell {...props}>{rowData.name}</Table.Cell>;

  return (
    <>
      <div className="d-flex w-75 mr-auto position-relative z-3 mb-2">
        <Rbac object={RbacObject.Supplier} action={'read:admin'}>
          <SupplierSelect />
        </Rbac>
      </div>

      <Table
        data={sortedAndFilteredData}
        sortColumn={sortColumn}
        sortType={sortType}
        loading={
          (isValidating && sortedAndFilteredData.length === 0) || loading
        }
        headerHeight={57}
        autoHeight
        onSortColumn={handleSortColumn}
      >
        <Table.Column width={150} fullText>
          <Table.HeaderCell>Поставщик</Table.HeaderCell>
          <SupplierCell />
        </Table.Column>
        <Table.Column width={150} fullText>
          <Table.HeaderCell>Откуда</Table.HeaderCell>
          <Table.Cell>
            {(rowData) => {
              return (rowData as CleanCustomsDeliveryMethod).supplier?.location
                ?.name;
            }}
          </Table.Cell>
        </Table.Column>

        <Table.Column flexGrow={1} minWidth={100} fullText sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Способ доставки
          </Table.HeaderCell>
          <DeliveryMethodCell dataKey="method" />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100} fullText sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Название
          </Table.HeaderCell>
          <DeliveryMethodNameCell dataKey="name" />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100} fullText sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Срок доставки
          </Table.HeaderCell>
          <PeriodCell dataKey="deliveryPeriodFrom" />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100} sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Включен
          </Table.HeaderCell>
          <ToggleCell
            dataKey="disabled"
            inverted={true}
            onChange={checkDeliveryMethod}
          />
        </Table.Column>
        <Table.Column>
          <Table.HeaderCell> </Table.HeaderCell>
          <ActionCell />
        </Table.Column>
      </Table>
    </>
  );
};
